import {
  AssetGroupDropdownInput,
  DeviceDropdownInput,
  EquipmentDropdownInput,
  FacilityDropdownInput,
  FlowDropdownInput,
} from "#batteries-included-components/Dropdowns";
import { BaseEntityDropdownProps } from "#batteries-included-components/Dropdowns/EntityDropdownInput";
import { useGetOneWorkflowTemplate } from "#src/components/hooks/adapters/useWorkflowTemplates";
import useLocalization from "#src/hooks/useLocalization";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  CheckboxInput,
  DateSelectorInput,
  DateVariant,
  Dialog,
  DialogProps,
  DropdownInput,
  Form,
  Skeleton,
  TextInput,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  AssetAdapter,
  AssetType,
  Resources,
  TriggerWorkflowFormSchema,
  TriggerWorkflowFormType,
  TriggerWorkflowVariablesType,
  WorkflowAdapter,
  WorkflowTemplateApiVersion,
} from "@validereinc/domain";
import {
  BaseError,
  DateFormats,
  isFiniteNumber,
  toTitleCaseString,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { add, format } from "date-fns";
import React, { useEffect, useMemo } from "react";
import styles from "./CreateWorkflowDialog.module.scss";

const cx = classNames.bind(styles);

const CREATE_WORKFLOW_DIALOG_TITLE = "Create Workflows";
const WORKFLOW_DIALOG_DESCRIPTION =
  "Workflows created manually will inherit all configurations set in the workflow's template including facility assignment and due date definition.";
const WORKFLOW_DIALOG_FORECAST_LABEL = "Forecast Workflow(s) Due Date";
const WORKFLOW_DIALOG_DUE_DATE_LABEL = "Workflow(s) Due Date";
const WORKFLOW_DUE_DATE_OPTIONS = [
  { label: "Hour(s) from creation", value: "hours" },
  { label: "Day(s) from creation", value: "days" },
  { label: "Week(s) from creation", value: "weeks" },
  { label: "Month(s) from creation", value: "months" },
];

export const CreateWorkflowDialog = ({
  onClose,
  templateId,
}: Pick<DialogProps, "onClose"> & {
  templateId: string;
}) => {
  const { toast } = useToast();
  const { localize } = useLocalization();
  const queryClient = useQueryClient();
  const form = useForm<TriggerWorkflowFormType>();

  const { data, isLoading: isTemplateDetailsLoading } =
    useGetOneWorkflowTemplate(
      {
        id: templateId,
      },
      { enabled: Boolean(templateId) }
    );

  const templateDetails = data?.data;
  const isV2Template =
    templateDetails?.api_version === WorkflowTemplateApiVersion.V2;
  const showAssetField = isTemplateDetailsLoading || !isV2Template;

  const templateDueDate = templateDetails?.triggers[0]?.due_date;
  useEffect(() => {
    form.setValue("has_due_date", !!templateDueDate);
  }, [templateDueDate]);

  const manualVariables = templateDetails?.initial_variables ?? {};

  const doManualVariablesExistOnTemplate =
    Object.keys(manualVariables).length > 0;

  const mutation = useMutation({
    mutationFn: async ({
      asset_ids,
      has_due_date,
      time_period,
      duration,
      initial_variables,
    }: TriggerWorkflowFormType) => {
      if (!templateDetails) {
        throw new BaseError("Workflow template details not available");
      }

      const workflowsToCreate = isV2Template
        ? [{ time_period, duration }]
        : asset_ids.map((asset_id) => ({
            asset_id,
            time_period,
            duration,
          }));

      const sanitizedVariables = Object.fromEntries(
        Object.entries(initial_variables ?? {})
          .map(([variableName, variableValue]) => {
            const variableDefinition =
              templateDetails.initial_variables[variableName];

            let sanitizedValue;

            if (
              variableDefinition.data_type === "date" ||
              variableDefinition.data_type === "date-time"
            ) {
              if (
                (typeof variableValue === "object" &&
                  "from" in variableValue) ||
                variableValue instanceof Date
              ) {
                if (variableValue instanceof Date)
                  sanitizedValue = variableValue.toISOString();
                else if (variableValue.from instanceof Date)
                  sanitizedValue = variableValue.from.toISOString();
              }
            }

            if (
              variableDefinition.data_type === "number" ||
              variableDefinition.data_type === "integer"
            ) {
              if (variableValue !== "") {
                const parsed = Number(variableValue);
                if (!Number.isNaN(parsed)) sanitizedValue = parsed;
              }
            }

            if (variableDefinition.data_type === "boolean") {
              sanitizedValue = Boolean(variableValue);
            }

            if (
              variableDefinition.data_type === "string" ||
              variableDefinition.data_type === "pick-list" ||
              variableDefinition.data_type === "multi-pick-list" ||
              variableDefinition.data_type === "lookup"
            ) {
              sanitizedValue = variableValue;
            }

            if (sanitizedValue === undefined) return null;

            return [
              variableName,
              {
                value: sanitizedValue,
                ...("format" in variableDefinition && variableDefinition.format
                  ? {
                      format:
                        "format" in variableDefinition
                          ? variableDefinition.format
                          : undefined,
                    }
                  : {}),
                data_type: variableDefinition.data_type,
              },
            ];
          })
          .filter(
            (entry): entry is [string, TriggerWorkflowVariablesType[string]] =>
              entry !== null
          )
      ) as TriggerWorkflowVariablesType;

      const promises = workflowsToCreate.map((workflow) =>
        WorkflowAdapter.trigger({
          ...(workflow.asset_id ? { asset_id: workflow.asset_id } : {}),
          time_period: has_due_date ? workflow.time_period : null,
          duration:
            workflow.duration &&
            isFiniteNumber(workflow.duration) &&
            has_due_date
              ? Number(workflow.duration)
              : null,
          workflow_template_id: templateId,
          ...(doManualVariablesExistOnTemplate &&
          initial_variables &&
          has_initial_variables
            ? { variables: sanitizedVariables }
            : {}),
        })
      );

      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [Resources.WORKFLOW, "getList"],
      });
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: [Resources.WORKFLOW, "getList"],
        });
      }, 4000);
      toast.push({
        intent: "success",
        description: "Successfully created workflow(s).",
      });

      onCloseCallback();
    },
    onError: (error: unknown) => {
      ExceptionUtils.reportException(error, "error", {
        hint: "Error creating workflow(s)",
      });
      toast.push({
        intent: "error",
        description: "Failed to create workflow(s).",
      });
    },
  });

  const assetTypeForDisplay = toTitleCaseString(
    localize(templateDetails?.asset_type ?? "-"),
    { intent: "ui" }
  );

  const onCloseCallback = () => {
    form.reset();

    onClose();
  };

  const onSubmit = form.handleSubmit((formValues) => {
    mutation.mutate(formValues);
  });

  const onFetchData = async ({
    value,
    pageSize,
    page,
    searchTerm,
  }: {
    value: string[];
    pageSize?: number;
    page?: number;
    searchTerm?: string;
  }) => {
    const filters = {
      $and: [
        templateDetails?.asset_filter,
        {
          ...(searchTerm && {
            [templateDetails?.asset_type + ".name"]: { $like: searchTerm },
          }),
          ...(value?.length > 0 && { id: { $in: value } }),
        },
      ],
      isAlreadyFormatted: true,
    };

    const { data } = await AssetAdapter.getList({
      pageSize,
      page,
      filters,
      sortBy: "name",
      sortDirection: "asc",
    });
    return data;
  };
  const {
    has_due_date,
    duration,
    time_period: timePeriod,
    has_initial_variables,
  } = form.watch();

  const sortedManualVariables = useMemo(() => {
    const sorted = Object.entries(manualVariables);
    sorted.sort((a, b) => a[1].order - b[1].order);
    return sorted;
  }, [manualVariables]);

  const renderManualVariables = () => {
    if (!doManualVariablesExistOnTemplate || !has_initial_variables) {
      return null;
    }

    return sortedManualVariables.map(([key, manualVariable]) => {
      if (manualVariable.data_type === "date") {
        let variant: DateVariant = "day";

        if (manualVariable.format === "YYYYMM") variant = "month";
        if (manualVariable.format === "YYYY") variant = "year";
        if (manualVariable.format === "YYYY-MM-DD") variant = "day";
        if (manualVariable.format === "ISO") variant = "time";

        return (
          <DateSelectorInput
            key={key}
            description={manualVariable.description}
            isRequired={manualVariable.is_required}
            name={`initial_variables.${key}`}
            label={manualVariable.display_name}
            variant={variant}
          />
        );
      }
      if (manualVariable.data_type === "date-time") {
        return (
          <DateSelectorInput
            key={key}
            description={manualVariable.description}
            isRequired={manualVariable.is_required}
            name={`initial_variables.${key}`}
            label={manualVariable.display_name}
            variant="time"
          />
        );
      }
      if (
        manualVariable.data_type === "pick-list" ||
        manualVariable.data_type === "multi-pick-list"
      ) {
        return (
          <DropdownInput
            key={key}
            description={manualVariable.description}
            isRequired={manualVariable.is_required}
            name={`initial_variables.${key}`}
            label={manualVariable.display_name}
            options={manualVariable.pick_list_values.map((value) => ({
              value,
              label: value,
            }))}
            valueKey="value"
            labelKey="label"
            isMulti={manualVariable.data_type === "multi-pick-list"}
          />
        );
      }
      if (
        manualVariable.data_type === "string" ||
        manualVariable.data_type === "number" ||
        manualVariable.data_type === "integer"
      ) {
        return (
          <TextInput
            key={key}
            description={manualVariable.description}
            isRequired={manualVariable.is_required}
            name={`initial_variables.${key}`}
            label={manualVariable.display_name}
            type={
              manualVariable.data_type === "number" ||
              manualVariable.data_type === "integer"
                ? "number"
                : "text"
            }
            step={manualVariable.data_type === "integer" ? 1 : undefined}
          />
        );
      }
      if (manualVariable.data_type === "boolean") {
        return (
          <CheckboxInput
            key={key}
            name={`initial_variables.${key}`}
            label={manualVariable.display_name}
            description={manualVariable.description}
            defaultValue={false}
          />
        );
      }
      if (manualVariable.data_type === "lookup") {
        const lookupProps = {
          key,
          description: manualVariable.description,
          isRequired: manualVariable.is_required,
          name: `initial_variables.${key}`,
          label: manualVariable.display_name,
          isMulti: false as const,
          fetchFilters: manualVariable.filter as
            | BaseEntityDropdownProps["fetchFilters"]
            | undefined,
        };

        switch (manualVariable.lookup_entity_type) {
          case AssetType.FACILITY:
            return (
              <FacilityDropdownInput
                {...lookupProps}
                isFluid
              />
            );
          case AssetType.EQUIPMENT:
            return (
              <EquipmentDropdownInput
                {...lookupProps}
                isFluid
              />
            );
          case AssetType.DEVICE:
            return (
              <DeviceDropdownInput
                {...lookupProps}
                isFluid
              />
            );
          case AssetType.FLOW:
            return (
              <FlowDropdownInput
                {...lookupProps}
                isFluid
              />
            );
          case AssetType.ASSET_GROUP:
            return (
              <AssetGroupDropdownInput
                {...lookupProps}
                isFluid
              />
            );
          default:
            return null;
        }
      }

      return null;
    });
  };

  return (
    <Dialog
      isOpen={!!templateId}
      onClose={onCloseCallback}
      title={CREATE_WORKFLOW_DIALOG_TITLE}
      actionRow={[
        <Button
          key="create-form-category-action"
          onClick={onSubmit}
          variant="primary"
          disabled={isTemplateDetailsLoading || !templateDetails}
          isLoading={mutation.isLoading}
        >
          {CREATE_WORKFLOW_DIALOG_TITLE}
        </Button>,
      ]}
    >
      <Form {...form}>
        <p>{WORKFLOW_DIALOG_DESCRIPTION}</p>
        {showAssetField ? (
          <Skeleton isLoading={isTemplateDetailsLoading}>
            <DropdownInput
              placeholder={"Select " + assetTypeForDisplay + "..."}
              label={assetTypeForDisplay}
              // @ts-expect-error TODO: onFetchData typing is weird
              onFetchData={onFetchData}
              name={TriggerWorkflowFormSchema.keyof().Enum.asset_ids}
              labelKey="name"
              valueKey="id"
              isRequired
              isMulti
            />
          </Skeleton>
        ) : null}
        <div className={cx("checkbox-container")}>
          <CheckboxInput
            name={TriggerWorkflowFormSchema.keyof().Enum.has_due_date}
            label="Workflow has a due date"
            isLabelShown={false}
          />
        </div>
        {has_due_date && (
          <>
            <label htmlFor="workflow-due-date">
              <strong id="workflow-due-date">
                {WORKFLOW_DIALOG_DUE_DATE_LABEL}
              </strong>
            </label>
            <div className={cx("dropdown-input-container")}>
              <TextInput
                className={cx("text-input")}
                defaultValue={templateDueDate ? templateDueDate.duration : 1}
                name={TriggerWorkflowFormSchema.keyof().Enum.duration}
                type="number"
                formatType="integer"
                isRequired
              />
              <DropdownInput
                className={cx("dropdown-input")}
                placeholder={WORKFLOW_DUE_DATE_OPTIONS[0].label}
                defaultValue={
                  templateDueDate
                    ? templateDueDate.time_period
                    : WORKFLOW_DUE_DATE_OPTIONS[0].value
                }
                options={WORKFLOW_DUE_DATE_OPTIONS}
                isSearchable={false}
                name={TriggerWorkflowFormSchema.keyof().Enum.time_period}
                valueKey="value"
                labelKey="label"
                isRequired
              />
            </div>
            <label htmlFor="workflow-forecast-due-date">
              {WORKFLOW_DIALOG_FORECAST_LABEL}
              <p id="workflow-forecast-due-date">
                <strong>
                  {format(
                    add(new Date(), {
                      [timePeriod ?? WORKFLOW_DUE_DATE_OPTIONS[0].value]:
                        duration,
                    }),
                    DateFormats.DATE_TIME
                  )}
                </strong>
              </p>
            </label>
          </>
        )}

        {doManualVariablesExistOnTemplate && (
          <div className={cx("checkbox-container")}>
            <CheckboxInput
              name={
                TriggerWorkflowFormSchema.keyof().Enum.has_initial_variables
              }
              label="Define workflow variables"
              isLabelShown={false}
            />
          </div>
        )}

        {renderManualVariables()}
      </Form>
    </Dialog>
  );
};
